import { deviceUses24HourClock } from '@lib/datetime'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'
import { TimePickerWrapper } from '../AddEventModal/FormComponents.styles'
import { EventCreateDraft, setDay, setStartTime } from './core/draft'
import { TripDays } from './core/presentation'
import { DayLine, Group, GroupBody, GroupHeading, GroupMarker, WhenStepForm } from './EventCreateFlow.styles'
import EventDayCalendar from './EventDayCalendar'

interface WhenStepProps {
	draft: EventCreateDraft
	onChange: (draft: EventCreateDraft) => void
	days: TripDays
	/** The selected day's name and place, as the itinerary's day header says them without the date; null when there's nothing to say. */
	dayLine: string | null
}

/**
 * Step 2 — When: the trip's days on a calendar in the create-trip calendar's
 * style (one is always selected, so Next is always on), the day's line under
 * it, and an optional start time — the whole step in the create-trip When
 * step's centred phone-width column.
 */
const WhenStep: React.FC<WhenStepProps> = ({ draft, onChange, days, dayLine }) => {
	const is24HourClock = deviceUses24HourClock()

	return (
		<WhenStepForm>
			<Group>
				<GroupHeading id="event-create-day">Select When It Is</GroupHeading>
				<GroupBody>
					<EventDayCalendar
						days={days}
						selectedDayIndex={draft.tripDayIndex}
						onPickDay={dayIndex => onChange(setDay(draft, dayIndex))}
						labelledBy="event-create-day"
					/>
					{dayLine && <DayLine>{dayLine}</DayLine>}
				</GroupBody>
			</Group>

			<Group>
				<GroupHeading>
					Start Time
					<GroupMarker>(optional)</GroupMarker>
				</GroupHeading>
				<GroupBody>
					<TimePickerWrapper>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<TimePicker
								value={draft.startTime ? dayjs(`2000-01-01T${draft.startTime}`) : null}
								onChange={(value: Dayjs | null) =>
									onChange(setStartTime(draft, value?.isValid() ? value.format('HH:mm') : null))
								}
								// Picker and display must agree (issue #164), and non-flight
								// display follows the viewer's clock (issue #165).
								ampm={!is24HourClock}
								format={is24HourClock ? 'HH:mm' : 'hh:mm A'}
								slotProps={{
									textField: { placeholder: 'Select time', fullWidth: true, 'aria-label': 'Start time' },
									// The ✕ that clears a set time.
									field: { clearable: true },
								}}
							/>
						</LocalizationProvider>
					</TimePickerWrapper>
				</GroupBody>
			</Group>
		</WhenStepForm>
	)
}

export default WhenStep
